// [NEW FILE] PM-18: dropdown with checkboxes, so a filter can hold several values at once.
//
//   <MultiSelect label="Department" allLabel="All departments" options={[{ value, label }]}
//                value={["2", "5"]} onChange={(values) => ...} pinned={[{ value: NONE, label: "No department" }]} />
//
// - The closed button shows a checkbox icon and "All departments", the one chosen name, or "Department: 3 selected".
// - Inside: a search box (always), "Select all", one checkbox per option, and "Clear".
// - Values inside one filter are combined with OR; the screen combines different filters with AND.
// - Options are sorted A to Z (numbers in number order); `pinned` options stay on top.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiSearch, FiCheckSquare } from "react-icons/fi";

const sortText = (a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: "base" });

export default function MultiSelect({
  label, allLabel, options = [], pinned = [], value = [], onChange, className = "", compact = false, keepOrder = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const listId = useId();
  const chosen = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
  const sorted = useMemo(() => (keepOrder ? options : [...options].sort(sortText)), [options, keepOrder]);
  const every = useMemo(() => [...pinned, ...sorted], [pinned, sorted]);
  const q = query.trim().toLowerCase();
  const shown = q ? every.filter((o) => String(o.label).toLowerCase().includes(q)) : every;
  const allShownChosen = shown.length > 0 && shown.every((o) => chosen.includes(o.value));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const toggle = (v) => onChange(chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v]);
  const toggleShown = () => {
    const ids = shown.map((o) => o.value);
    onChange(allShownChosen ? chosen.filter((v) => !ids.includes(v)) : [...new Set([...chosen, ...ids])]);
  };

  let summary = allLabel || `All ${label}`;
  if (chosen.length === 1) summary = every.find((o) => o.value === chosen[0])?.label || `${label}: 1 selected`;
  else if (chosen.length > 1) summary = `${label}: ${chosen.length} selected`;

  return (
    <div className={`multi-select ${compact ? "is-compact" : ""} ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`form-control multi-select-button ${chosen.length ? "is-filtered" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${label}: ${summary}`}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        {/* The checkbox icon shows at a glance that several values can be ticked. */}
        <FiCheckSquare size={14} aria-hidden="true" className="multi-select-icon" />
        <span className="multi-select-summary">{summary}</span>
        {chosen.length > 1 && <span className="multi-select-count" aria-hidden="true">{chosen.length}</span>}
        <FiChevronDown size={15} aria-hidden="true" />
      </button>
      {open && (
        <div className="multi-select-pop" role="dialog" aria-label={`Choose ${label}`}>
          {/* [UPDATED] Requirement 2: the search box is always shown (was: from 7 options). */}
          {every.length > 0 && (
            <label className="multi-select-search">
              <FiSearch size={14} aria-hidden="true" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${String(label).toLowerCase()}`} aria-label={`Search ${label}`} />
            </label>
          )}
          <ul id={listId} className="multi-select-list" role="listbox" aria-multiselectable="true" aria-label={label}>
            {shown.length > 0 && (
              <li className="multi-select-all">
                <label>
                  <input type="checkbox" checked={allShownChosen} onChange={toggleShown} />
                  <span>{q ? "Select all shown" : "Select all"} ({shown.length})</span>
                </label>
              </li>
            )}
            {shown.map((o) => (
              <li key={o.value} role="option" aria-selected={chosen.includes(o.value)}>
                <label>
                  <input type="checkbox" checked={chosen.includes(o.value)} onChange={() => toggle(o.value)} />
                  <span>{o.label}</span>
                  {o.hint && <small className={`multi-select-hint ${o.hintTone === "full" ? "is-full" : ""}`}>{o.hint}</small>}
                </label>
              </li>
            ))}
            {!shown.length && <li className="multi-select-empty">Nothing matches "{query}".</li>}
          </ul>
          <div className="multi-select-foot">
            <button type="button" className="link-btn" onClick={() => onChange([])} disabled={!chosen.length}>Clear</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}